import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import automationImg from '../assets/capabilities/automation.png';
import aiIntegrationImg from '../assets/capabilities/integration.png';
import chatbotImg from '../assets/capabilities/chat-bot.png';
import machineLearningImg from '../assets/capabilities/machine-learning.png';

const features = [
    {
        img: automationImg,
        key: 'automation',
        title: 'capabilities.items.automation.title',
        description: 'capabilities.items.automation.title',
        detailedText: 'capabilities.items.automation.detailedText',
    },
    {
        img: aiIntegrationImg,
        key: 'integration',
        title: 'capabilities.items.integration.title',
        description: 'capabilities.items.integration.title',
        detailedText: 'capabilities.items.integration.detailedText',
    },
    {
        img: chatbotImg,
        key: 'chatbots',
        title: 'capabilities.items.chatbots.title',
        description: 'capabilities.items.chatbots.title',
        detailedText: 'capabilities.items.chatbots.detailedText',
    },
    {
        img: machineLearningImg,
        key: 'ml',
        title: 'capabilities.items.ml.title',
        description: 'capabilities.items.ml.title',
        detailedText: 'capabilities.items.ml.detailedText',
    },
];

const FeatureCard = ({ img, title, description, detailedText }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Card
            elevation={0}
            sx={{
                p: { xs: 4, sm: 5 },
                height: '100%',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: '20px',
                boxShadow: `0 4px 16px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'}`,
                transition: 'all 0.4s cubic-bezier(0.25, 0.57, 0.33, 0.99)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    boxShadow: `0 12px 40px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)'}`,
                    transform: 'translateY(-8px)',
                    borderColor: theme.palette.primary.main,
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}08, transparent)`,
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.4s ease',
                    zIndex: 0,
                },
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Stack spacing={3} sx={{ height: '100%', position: 'relative', zIndex: 1 }}>
                    {/* Icon */}
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '16px',
                            backgroundColor: theme.palette.background.paper,
                            border: `2px solid ${theme.palette.grey[200]}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: theme.palette.primary.main + '05',
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src={img}
                            alt={title}
                            loading="lazy"
                            sx={{
                                width: 32,
                                height: 32,
                                filter: 'none',
                                transition: 'transform 0.3s ease',
                                transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                            }}
                        />
                    </Box>
                
                {/* Title */}
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: '1.25rem',
                        lineHeight: 1.3,
                        transition: 'color 0.3s ease',
                        ...(isHovered && {
                            color: theme.palette.primary.main,
                        }),
                    }}
                >
                    {t(title)}
                </Typography>
                
                {/* Description */}
                <Typography
                    variant="body2"
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        flex: 1,
                        transition: 'color 0.3s ease',
                    }}
                >
                    {t(detailedText)}
                </Typography>
            </Stack>
        </Card>
    );
};

export default function Capabilities() {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Box
            component="section"
            id="capabilities"
            sx={{
                py: { xs: 8, sm: 12, md: 16 },
                backgroundColor: theme.palette.background.default,
            }}
        >
            <Container maxWidth="lg">
                {/* Section Header */}
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            fontSize: {
                                xs: '2.25rem',
                                sm: '2.75rem',
                                md: '3.25rem',
                            },
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            mb: 2,
                        }}
                    >
                        {t('capabilities.title')}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '1.25rem',
                            maxWidth: '600px',
                            mx: 'auto',
                        }}
                    >
                        Comprehensive AI solutions designed to transform your business operations
                    </Typography>
                </Box>

                {/* Features Grid */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            lg: 'repeat(4, 1fr)',
                        },
                        gap: { xs: 3, md: 4 },
                    }}
                >
                    {features.map(({ img, key, title, description, detailedText }) => (
                        <FeatureCard
                            key={key}
                            img={img}
                            title={title}
                            description={description}
                            detailedText={detailedText}
                        />
                    ))}
                </Box>
            </Container>
        </Box>
    );
}